// Suppliers API
import api, { type AxiosResponse } from './client';
import type {
  SupplierProfile,
  SupplierDashboardStats,
  SupplierPerformanceMetric,
  SupplierCertification,
  SupplierMessage,
  CreditScore,
  QueryParams,
  SupplierSelfPerformanceRow,
  SupplierCustomerSite,
} from './types';

/**
 * CR-6685: one existing-supplier match from the Add Approved Supplier typeahead.
 * Carries the full field set the modal auto-populates from, plus the logo.
 */
export interface ExistingSupplierMatch {
  supplier_id: number;
  company_name: string;
  logo_url: string;
  supplier_code: string;
  registration_number: string;
  primary_email: string;
  primary_phone: string;
  website: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  payment_terms: string;
  currency: string;
  annual_revenue: string;
  employee_count: string;
  year_established: string;
}

export const suppliersAPI = {
  // Supplier Profiles
  getProfiles: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierProfile[] }>> =>
    api.get('/suppliers/profiles/', { params }),

  // SI-16: supplier-side page — the caller's own row once per active customer.
  getByCustomer: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierSelfPerformanceRow[] }>> =>
    api.get('/suppliers/profiles/by-customer/', { params }),

  // CR-5977 follow-up: accept view-scoping query params (view_level,
  // group, company, site) so the supplier detail header narrows its
  // OTD / PPM / NCR aggregates to the same customer set as the OTD
  // drilldown. Without these params the live serializer falls back to
  // its un-scoped aggregate, which is what made the detail header
  // disagree with the drilldown on helix-testing.
  getProfile: (id: number, params?: QueryParams): Promise<AxiosResponse<SupplierProfile>> =>
    api.get(`/suppliers/profiles/${id}/`, { params }),

  updateProfile: (id: number, data: Partial<SupplierProfile>): Promise<AxiosResponse<SupplierProfile>> =>
    api.patch(`/suppliers/profiles/${id}/`, data),

  // CR-6685: typeahead over existing approved suppliers (org-scoped) for the
  // Add Approved Supplier modal — pick a match to link it instead of duplicating.
  searchExistingSuppliers: (q: string, limit = 10): Promise<AxiosResponse<{ results: ExistingSupplierMatch[] }>> =>
    api.get('/suppliers/profiles/search-existing/', { params: { q, limit } }),

  getDashboardStats: (params?: QueryParams): Promise<AxiosResponse<SupplierDashboardStats>> =>
    api.get('/suppliers/profiles/dashboard_stats/', { params }),

  getTopRated: (): Promise<AxiosResponse<SupplierProfile[]>> =>
    api.get('/suppliers/profiles/top_rated/'),

  // Performance Metrics
  getMetrics: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierPerformanceMetric[] }>> =>
    api.get('/suppliers/metrics/', { params }),

  // Certifications
  getCertifications: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierCertification[] }>> =>
    api.get('/suppliers/certifications/', { params }),

  // CR-3375: Supplier OTIF Settings
  getOtifSettings: (profileId: number): Promise<AxiosResponse<{
    supplier_id: number;
    supplier_name: string;
    use_custom_otif_window: boolean;
    otif_days_early_allowed: number;
    otif_days_late_allowed: number;
  }>> =>
    api.get(`/suppliers/profiles/${profileId}/otif-settings/`),

  updateOtifSettings: (profileId: number, data: {
    use_custom_otif_window?: boolean;
    otif_days_early_allowed?: number;
    otif_days_late_allowed?: number;
  }): Promise<AxiosResponse<{
    status: string;
    message: string;
    data: {
      supplier_id: number;
      supplier_name: string;
      use_custom_otif_window: boolean;
      otif_days_early_allowed: number;
      otif_days_late_allowed: number;
    };
  }>> =>
    api.patch(`/suppliers/profiles/${profileId}/otif-settings/`, data),

  // Stock Levels
  getStockLevels: (params?: {
    supplier?: number;
    customer?: number;
    part_number?: string;
    search?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{
    count: number;
    results: SupplierStockLevel[];
  }>> =>
    api.get('/suppliers/stock-levels/', { params }),

  getStockLevel: (id: number): Promise<AxiosResponse<SupplierStockLevel>> =>
    api.get(`/suppliers/stock-levels/${id}/`),

  createStockLevel: (data: {
    customer: number;
    part_number: string;
    quantity_available: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<AxiosResponse<SupplierStockLevel>> =>
    api.post('/suppliers/stock-levels/', data),

  updateStockLevel: (id: number, data: {
    quantity_available?: number;
    unit_of_measure?: string;
    notes?: string;
  }): Promise<AxiosResponse<SupplierStockLevel>> =>
    api.patch(`/suppliers/stock-levels/${id}/`, data),

  deleteStockLevel: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/suppliers/stock-levels/${id}/`),

  bulkUploadStockLevels: (file: File): Promise<AxiosResponse<{
    success: boolean;
    created: number;
    updated: number;
    errors: Array<{ row: number; errors: Record<string, string[]>; data: Record<string, string> }>;
    total_errors: number;
  }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/suppliers/stock-levels/bulk-upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  downloadStockTemplate: (): Promise<AxiosResponse<Blob>> =>
    api.get('/suppliers/stock-levels/download-template/', {
      responseType: 'blob',
    }),

  exportStockLevels: (params?: { part_number?: string }): Promise<AxiosResponse<Blob>> =>
    api.get('/suppliers/stock-levels/export/', {
      params,
      responseType: 'blob',
    }),

  // Create supplier from onboarding wizard
  createFromWizard: (data: {
    company_name: string;
    company_address?: string;
    // CR-5390: structured address fields. Backend prefers these over the
    // legacy company_address single-line field when both are present.
    address_line_1?: string;
    address_line_2?: string;
    address_city?: string;
    address_region?: string;
    address_postcode?: string;
    address_country?: string;
    /** The supplier organisation's own email → Company.primary_email. Distinct
     *  from the primary contact's address, which stays on the contact. Set by
     *  the onboarding wizard's Company Email field; the create-modal's
     *  primary_email below is the same destination on a different flow. */
    company_email?: string;
    // CR-6681: identity / contact / payment fields the "Add Approved Supplier"
    // modal collects. Previously dropped on create, so they never persisted.
    supplier_code?: string;
    registration_number?: string;
    primary_email?: string;
    primary_phone?: string;
    website?: string;
    payment_terms?: string;
    annual_revenue?: string;
    employee_count?: string;
    year_established?: string;
    description?: string;
    currency?: string;
    annual_spend?: string;
    logo_url?: string;
    customer_id: number;
    customer_ids?: number[];
    // CR-6366 alignment: per-customer supplier code + currency. Keyed by
    // customer id; each selected site's own values are written to its
    // relationship. A missing/blank entry falls back to the single defaults.
    customer_settings?: Record<string, { supplier_code?: string; currency?: string }>;
    // CR-6654: when true the supplier is created already approved (the
    // "Add Approved Supplier" quick-add) instead of as an onboarding draft.
    approved?: boolean;
    // CR-6685: when set, link this EXISTING supplier company to the customer
    // site(s) instead of creating a new one (no duplicate record).
    existing_supplier_id?: number;
  }): Promise<AxiosResponse<{
    supplier_id: number;
    company_name: string;
    onboarding_status: string;
    linked_existing?: boolean;
  }>> =>
    api.post('/suppliers/profiles/create-from-wizard/', data),

  // CR-6695: Default Login Type restriction. The platform determines whether
  // the supplier being onboarded is an intercompany supplier (supplier group
  // in the same root organisation tree as a linked customer company); only
  // then may wizard Step 3 offer the Customer login type.
  checkIntercompanyEligibility: (params: {
    supplier_group_id: number;
    customer_ids: number[];
  }): Promise<AxiosResponse<{ is_intercompany: boolean }>> =>
    api.get('/suppliers/intercompany-eligibility/', {
      params: {
        supplier_group_id: params.supplier_group_id,
        customer_ids: params.customer_ids.join(','),
      },
    }),

  // Save wizard draft state for resuming onboarding wizard
  saveWizardDraft: (companyId: number, data: {
    form_data: Record<string, unknown>;
    current_step: number;
    highest_step: number;
  }): Promise<AxiosResponse<{ status: string; wizard_draft: Record<string, unknown> }>> =>
    api.patch(`/suppliers/profiles/${companyId}/save-draft/`, data),

  // CR-4056: Toggle supplier active/inactive
  toggleSupplierActive: (id: number, isActive?: boolean): Promise<AxiosResponse<{ status: string; is_active: boolean; message: string }>> =>
    api.post(`/suppliers/profiles/${id}/toggle-active/`, isActive !== undefined ? { is_active: isActive } : {}),

  // CR-6654: staff/superuser maintenance. Delete removes the supplier company,
  // profile and relationships; it is refused (400) when the supplier carries
  // transactional data.
  deleteSupplier: (companyId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/suppliers/profiles/${companyId}/delete-supplier/`),

  // CR-4161: Credit Scores
  getCreditScores: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CreditScore[] }>> =>
    api.get('/suppliers/credit-scores/', { params }),

  createCreditScore: (data: FormData): Promise<AxiosResponse<CreditScore>> =>
    api.post('/suppliers/credit-scores/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateCreditScore: (id: number, data: FormData): Promise<AxiosResponse<CreditScore>> =>
    api.patch(`/suppliers/credit-scores/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteCreditScore: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/suppliers/credit-scores/${id}/`),

  // CR-5771: Stream the attachment through an auth-gated endpoint so the
  // View Document link works in dev (where /media/credit_scores/* is blocked)
  // and so the response carries the user's Token header.
  downloadCreditScoreAttachment: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/suppliers/credit-scores/${id}/download/`, { responseType: 'blob' }),

  // CR-4146: UK Companies House search
  searchCompaniesHouse: (query: string): Promise<AxiosResponse<{
    results: Array<{
      registration_number: string;
      company_name: string;
      address: string;
      incorporated_on: string;
      company_status: string;
      company_type: string;
    }>;
  }>> =>
    api.get('/suppliers/companies-house/search/', { params: { q: query } }),

  // Performance Matrix - monthly OTIF ratings per supplier
  // Callers forward the view scope and the page's filters alongside the year
  // (and, for a per-site breakdown row, ``site`` + ``company_id``).
  getPerformanceMatrix: (params?: Record<string, string | number | boolean | undefined>): Promise<AxiosResponse<PerformanceMatrixResponse>> =>
    api.get('/suppliers/performance-matrix/', { params }),

  // Supplier Messages (internal communication)
  getMessages: (params: { supplier: number; customer: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: SupplierMessage[] }>> =>
    api.get('/suppliers/messages/', { params }),

  sendMessage: (data: { supplier: number; customer: number; content: string }): Promise<AxiosResponse<SupplierMessage>> =>
    api.post('/suppliers/messages/', data),

  // CR-QBR: QBR Schedule endpoints
  getQBRSchedule: (supplierId: number): Promise<AxiosResponse<QBRScheduleResponse>> =>
    api.get(`/suppliers/${supplierId}/qbr-schedule/`),

  recordQBR: (supplierId: number, data: { contract_id: number; qbr_date: string; notes?: string }): Promise<AxiosResponse<{
    message: string;
    contract_id: number;
    last_qbr_date: string;
    next_qbr_date: string | null;
  }>> =>
    api.post(`/suppliers/${supplierId}/qbr-record/`, data),
};

type MatrixMonth =
  | 'Jan' | 'Feb' | 'Mar' | 'Apr' | 'May' | 'Jun'
  | 'Jul' | 'Aug' | 'Sep' | 'Oct' | 'Nov' | 'Dec';

export type PerformanceMatrixRow = {
  supplier_id: number;
  Supplier: string;
  SupplierCode: string;
  Business: string;
  OverallRating: string;
  OverallPct: number | null;
  // CR-6312 rework: the blended score + PPM behind each grade, so the UI
  // can explain a cell. Optional — older deploys omit them.
  OverallScore?: number | null;
  OverallPpm?: number | null;
  TotalLines: number;
  OtifLines: number;
  /** Customer review 11 Sep (msg 3/4/17): the customer sites the row's grades
   *  cover. `Business` / `SupplierCode` describe this set ("All sites (N)" and
   *  no code when there are several). */
  customer_sites?: SupplierCustomerSite[];
} & { [K in MatrixMonth]: string }
  & { [K in `${MatrixMonth}_pct`]: number | null }
  & { [K in `${MatrixMonth}_score`]?: number | null }
  & { [K in `${MatrixMonth}_ppm`]?: number | null };

// CR-6312 rework: the grading scale the backend actually used, served with
// the rows so the legend, the cells and every role share one scale (the
// legend previously depended on an admin-only settings endpoint).
export interface MatrixRatingBand {
  grade: string;
  /** The band's configured display label (Admin Setup → Grade Thresholds). */
  label: string;
  min_score: number;
  max_score?: number;
  /** Palette-resolved colour — the same as the Table's `effective_color`. */
  color: string;
  order: number;
}

export interface PerformanceMatrixResponse {
  year: number;
  rows: PerformanceMatrixRow[];
  total: number;
  rating_bands?: MatrixRatingBand[];
  // CR-5883: optional fiscal-year metadata. Older deploys without the
  // resolver omit these fields, so callers must default to the calendar
  // Jan-Dec column order when month_keys is absent.
  year_type?: 'fiscal' | 'calendar';
  fiscal_year_end_month?: number;
  month_keys?: string[];
  year_label?: string;
}

// CR-QBR: QBR Schedule types
export interface QBRScheduleItem {
  contract_id: number;
  contract_number: string;
  customer_id: number;
  customer_name: string | null;
  review_frequency_months: number;
  review_frequency_display: string;
  last_review_date: string | null;
  next_review_date: string | null;
  owner_id: number | null;
  owner_name: string | null;
  status: 'pending' | 'scheduled' | 'completed' | 'overdue';
  days_until_review: number | null;
}

export interface QBRScheduleResponse {
  has_schedule: boolean;
  schedule_items: QBRScheduleItem[];
  message: string | null;
}

// Stock Level type
export interface SupplierStockLevel {
  id: number;
  supplier: number;
  supplier_name: string;
  customer: number;
  customer_name: string;
  part_number: string;
  description: string;
  quantity_available: number;
  unit_of_measure: string;
  last_updated: string;
  updated_by: number | null;
  updated_by_name: string | null;
  notes: string;
  /** True when call-off is enabled for the (supplier, customer) pair —
   *  the row is read-only on the Stock Levels page in that case. */
  call_off_enabled: boolean;
  created_at: string;
}
